import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { LEVEL_THRESHOLDS, levelForPoints, pointsToNextLevel } from "../lib/community";
import { watchMembers, type CommunityMember } from "../lib/firebase";
import { adminUids } from "../lib/firebase.config";
import { MemberAvatar } from "../components/CommunityGate";

const TOP_N = 30;
const FREE_N = 5; // Top 5 learn the bootcamp free

export function LeaderboardPage() {
  const { user } = useAuth();
  const [members, setMembers] = useState<CommunityMember[] | null>(null);

  useEffect(() => watchMembers(setMembers), []);

  if (!user) return null;
  if (members === null) return <p style={{ padding: "3rem", color: "var(--c-text-muted)" }}>กำลังโหลด…</p>;

  const me = members.find((m) => m.uid === user.uid);
  const myPoints = me?.points ?? 0;
  const myLevel = levelForPoints(myPoints);
  const toNext = pointsToNextLevel(myPoints);
  // Admins (Dew) never compete for their own bootcamp prize.
  const sorted = members.filter((m) => !adminUids.includes(m.uid) && !m.hidden).sort((a, b) => b.points - a.points);
  const ranked = sorted.slice(0, TOP_N);
  const myRank = sorted.findIndex((m) => m.uid === user.uid) + 1; // 0 = not on the board yet
  const inTop = myRank > 0 && myRank <= TOP_N;
  // Points needed to pass whoever is at #30 right now (ties don't count, so +1).
  const toTop = sorted.length < TOP_N ? 0 : Math.max(0, sorted[TOP_N - 1].points - myPoints + 1);

  return (
    <main className="container-tight">
      {/* Bootcamp gimmick (Dew, 2026-10-18): Top 5 learn free, 3 more free seats drawn from the rest of the Top 30 (no discount). */}
      <section className="cm-promo">
        <span className="kicker">ของรางวัลลีดเดอร์บอร์ด</span>
        <h2>Mini Bootcamp: AIOS For One Person Business</h2>
        <p>ดิวจะเปิดบูตแคมป์นี้ต้นปีหน้า</p>
        <ol>
          <li><strong>Top {FREE_N}</strong> ได้เรียนฟรี</li>
          <li>คนอื่นใน <strong>Top {TOP_N}</strong> ลุ้นสุ่มแจกเรียนฟรีอีก 3 ท่าน</li>
        </ol>
        {!adminUids.includes(user.uid) && <p className="cm-promo__me">
          {myRank > 0 && myRank <= FREE_N
            ? `ตอนนี้คุณอยู่อันดับ ${myRank} ติด Top ${FREE_N} ได้เรียนฟรี รักษาไว้นะ`
            : inTop
            ? `ตอนนี้คุณอยู่อันดับ ${myRank} ติด Top ${TOP_N} แล้ว (ได้ลุ้นสุ่มเรียนฟรี) ขยับอีกนิดจะติด Top ${FREE_N}`
            : `ตอนนี้คุณ${myRank ? `อยู่อันดับ ${myRank}` : "ยังไม่มีอันดับ"} อีก ${toTop} แต้มจะติด Top ${TOP_N}`}
        </p>}
      </section>

      <section className="cm-mylevel">
        <MemberAvatar name={me?.displayName || user.displayName || ""} points={myPoints} avatar={me?.avatar} />
        <div>
          <h1>เลเวล {myLevel}</h1>
          <p className="cm-post__meta">
            {myPoints} แต้ม · {toNext === null ? "เลเวลสูงสุดแล้ว" : `อีก ${toNext} แต้มถึงเลเวล ${myLevel + 1}`}
          </p>
          <p className="cm-post__meta">
            เรียนจบ {me?.lessonsDone ?? 0} บท · อยู่ในเว็บ {Math.floor((me?.minutesActive ?? 0) / 60)} ชม. {(me?.minutesActive ?? 0) % 60} นาที
          </p>
          <p className="cm-post__meta">เรียนจบ 1 บท = 10 แต้ม · ถามคำถามที่สงสัย = 25 แต้ม · ทำควิซ/การบ้าน = 25 แต้ม / ชิ้น</p>
        </div>
      </section>

      <ol className="cm-levels" aria-label="เลเวลทั้งหมด">
        {LEVEL_THRESHOLDS.map((min, i) => {
          const count = members.filter((m) => levelForPoints(m.points) === i + 1).length;
          return (
            <li key={i} className={i + 1 === myLevel ? "cm-levels__me" : ""}>
              <strong>เลเวล {i + 1}</strong>
              <span>{min}+ แต้ม · {count} คน</span>
            </li>
          );
        })}
      </ol>

      <h2 style={{ margin: "2rem 0 1rem" }}>Top {TOP_N}</h2>
      <ol className="cm-board">
        {ranked.map((m, i) => (
          <li key={m.uid} className={m.uid === user.uid ? "cm-board__me" : ""}>
            <span className="cm-board__rank">{i + 1}</span>
            <MemberAvatar name={m.displayName} points={m.points} avatar={m.avatar} />
            <span className="cm-board__name">
              {m.displayName}
              <small className="cm-post__meta" style={{ display: "block", fontWeight: 400 }}>
                จบ {m.lessonsDone} บท · {Math.floor(m.minutesActive / 60)} ชม.
              </small>
            </span>
            <span className="cm-board__pts">+{m.points}</span>
          </li>
        ))}
      </ol>
    </main>
  );
}
